import { MFChannel } from '../../Components/SDK';
import Store from '../../Store';
import {
   appendChannelMessages,
   clearChannelConversation,
   getChannelInfo,
   setChannelHasMore,
   setChannelLoading,
   setUserInfoMap,
} from '../../Slice';

const PAGE_SIZE = 20;
const SYSTEM_MESSAGE_TYPES = new Set(['1', '2', '3', '4', '5']);

// Filter out system messages and deleted messages
const filterMessages = messages => {
   const result = [];
   for (const msg of messages) {
      if (!SYSTEM_MESSAGE_TYPES.has(msg.messageType) && !msg.isDeletedForEveryone && !msg.isDeletedForMe) {
         result.push({ ...msg, type: 'message' });
      }
   }
   return result;
};

// Insert date labels between messages
const insertDateLabels = messages => {
   if (!messages || messages.length === 0) return [];

   const result = [];

   // Get date key from timestamp (prefer messageTime, fallback to createdAt)
   const getDateKey = msg => {
      const ts = msg.messageTime || msg.createdAt || Date.now();
      const d = new Date(ts);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
   };

   // Get label for a date key string (YYYY-MM-DD format)
   const getLabel = dateKey => {
      const today = new Date();
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

      // Parse dateKey (YYYY-MM-DD) in local timezone
      const [year, month, day] = dateKey.split('-').map(Number);
      const msgDate = new Date(year, month - 1, day);

      const yesterdayOnly = new Date(todayOnly);
      yesterdayOnly.setDate(todayOnly.getDate() - 1);

      if (msgDate.getTime() === todayOnly.getTime()) return 'Today';
      if (msgDate.getTime() === yesterdayOnly.getTime()) return 'Yesterday';

      return new Intl.DateTimeFormat('en-US', {
         month: 'long',
         day: '2-digit',
         year: 'numeric',
      }).format(msgDate);
   };

   // Traverse in order (newest → oldest)
   for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const dateKey = getDateKey(msg);
      const nextMsg = messages[i + 1];
      const nextDateKey = nextMsg ? getDateKey(nextMsg) : null;

      // Push the message
      result.push(msg);

      // If next message is from a different date OR it's the last message
      if (!nextMsg || dateKey !== nextDateKey) {
         const label = {
            type: 'date-label',
            label: getLabel(dateKey),
            dateKey,
            messageId: `date-${new Date(dateKey).getTime()}-${i}`,
         };
         result.push(label);
      }
   }

   return result;
};

/**
 * Completely refreshes channel messages
 * Clears existing messages and fetches fresh data from the server
 * @param {string} channelId - The channel ID to refresh
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const refreshChannelMessages = async channelId => {
   if (!channelId) {
      console.error('refreshChannelMessages: channelId is required');
      return false;
   }

   const dispatch = Store.dispatch;

   try {
      // Step 1: Clear existing channel conversation data
      dispatch(clearChannelConversation({ channelId }));

      // Step 2: Set loading state
      dispatch(setChannelLoading({ channelId, direction: 'initialLoad', value: true }));

      // Step 3: Fetch fresh messages from server
      const channelInfo = getChannelInfo(channelId);
      const sequenceId = channelInfo?.lastReadMessage?.sequenceId || 0;

      const response = await MFChannel.fetchChannelMessages(channelId, {
         sequenceId,
         direction: 'backward',
         size: PAGE_SIZE,
      });

      // Step 4: Validate response
      if (response?.statusCode !== 200) {
         console.error('refreshChannelMessages: Failed to fetch messages', response);
         return false;
      }

      const messages = response.messages || [];
      if (!Array.isArray(messages)) {
         console.error('refreshChannelMessages: Invalid messages format');
         return false;
      }

      // Step 5: Process messages
      const filteredMessages = filterMessages(messages);
      const messagesWithDateLabels = insertDateLabels(filteredMessages);

      // Step 6: Update profile details if available
      if (response.profileDetails) {
         dispatch(setUserInfoMap(response.profileDetails));
      }

      // Step 7: Dispatch messages to Redux
      if (messagesWithDateLabels.length > 0) {
         dispatch(
            appendChannelMessages({
               channelId,
               messages: messagesWithDateLabels,
            }),
         );
      }

      // Step 8: Update hasMore status
      const hasMoreData = messages.length === PAGE_SIZE;
      dispatch(
         setChannelHasMore({
            channelId,
            direction: 'backward',
            value: hasMoreData,
         }),
      );

      return true;
   } catch (error) {
      console.error('refreshChannelMessages: Error refreshing channel messages', error);
      return false;
   } finally {
      // Always clear loading state
      dispatch(setChannelLoading({ channelId, direction: 'initialLoad', value: false }));
   }
};
